// Eskom PARCC — bulk transaction UPDATE parser (Lindiwe R6, 23 Jul 2026).
//
// PURE (no React, no Firebase). Lindiwe: "We would like to be able to update
// transactions via Excel bulk upload. The same mechanism as a reviewer manually
// updating a transaction, except done via bulk Excel."
//
// The file shape is the ordinary population workbook (feedback/Bulk Upload
// Template.xlsx and her Upload080707.xlsx), so this REUSES the import parser
// wholesale — `parseWorkbook`, `matrixToRecords`, the `ALIASES` map,
// `resolveField`, `splitFindings`, `findingFromText`. Nothing about header
// detection or column aliasing is reimplemented here; a fork would drift.
//
// THE ONE RULE THAT MAKES A WIDE BLAST RADIUS SAFE: this parser emits only the
// columns the workbook PHYSICALLY CARRIES. `presentColumns` is computed from the
// header row, not from the payload — so a column the file omits is never in the
// update, and a BLANK CELL in a column the file does carry is a deliberate no-op
// rather than a clear. (Clearing a field stays a manual edit: a spreadsheet
// cannot distinguish "leave this alone" from "empty this", and guessing wrong at
// 300 rows is the 1 Jul clobber class all over again.)
import { inferDataset } from '../../domain/dataset';
import { parseRand, formatRand } from '../../lib/money';
import { normalizeGapStatus } from '../../domain/normalize';
import { reviewedFlagForStatus } from '../../domain/reviewStatus';
import { BULK_UPDATE_COLUMNS, type BulkUpdateColumn } from '../../lib/importColumns';
import {
  parseWorkbook,
  matrixToRecords,
  detectHeaderRow,
  normalizeHeader,
  resolveField,
  splitFindings,
  findingFromText,
  ALIASES,
  type ImportRecord,
} from '../import/importParse';
import type { TransactionInsertInput, FindingInsertInput } from '../../lib/dbWrite';
import { normalisePeriod } from '../../domain/fiscal';

/** One row of a bulk-update workbook, mapped to the fields it actually carries. */
export interface BulkUpdateRow {
  /** 1-based position among the data rows, for operator-facing messages. */
  rowNo: number;
  /** The match key. Empty when the row carries no usable reference. */
  referenceNo: string;
  /**
   * The update payload — ONLY the columns this workbook carries. `referenceNo`
   * rides along as the key; the write mapper strips it.
   */
  patch: TransactionInsertInput;
  /** Finding lines derived from the file's findings column, if it has one. */
  findings: FindingInsertInput[];
  /** True when the workbook carries a findings column at all (drives the merge). */
  carriesFindings: boolean;
  /** Per-row notes: cells recognised but not writable, values that did not parse. */
  warnings: string[];
}

export interface BulkUpdateParse {
  rows: BulkUpdateRow[];
  /** The update columns this workbook carries, in whitelist order. */
  presentColumns: BulkUpdateColumn[];
  /**
   * Headers the file carries that map to a real transaction field the connector
   * CANNOT change on an update (referenceNo/baseNo/recordLabel/year are
   * insert-only). Surfaced so an operator sees the cells were read and ignored,
   * rather than wondering why Year never moved. Period left this set in Aug
   * 2026 (owner feedback: unable to update period) — it is writable now.
   */
  insertOnlyColumns: string[];
  /** Headers that matched nothing — informational, never an error. */
  ignoredHeaders: string[];
}

/**
 * Canonical import-field name → the transaction column it writes. Only fields
 * that map to a BULK_UPDATE_COLUMNS entry appear; the rest are either
 * insert-only ({@link INSERT_ONLY_FIELDS}) or not a transaction column at all.
 */
const FIELD_TO_COLUMN: ReadonlyArray<readonly [canonical: string, column: BulkUpdateColumn]> = [
  ['Dataset', 'dataset'],
  ['Period', 'period'],
  ['Division', 'division'],
  ['Supplier', 'supplier'],
  ['CSD Number', 'csdNumber'],
  ['Value', 'value'],
  ['Mechanism', 'mechanism'],
  ['Link', 'link'],
  ['OU', 'ou'],
  ['Contract Number', 'contractNumber'],
  ['Link Provided', 'linkProvided'],
  ['Link Issue', 'linkIssue'],
  ['Panel Release', 'panel'],
  ['BU', 'bu'],
  ['Brief Description', 'briefDescription'],
  ['Buyer', 'buyer'],
  ['PR Number', 'prNumber'],
  ['Procurement Manager', 'procurementManager'],
  ['End-User', 'endUser'],
  ['Self-declaration Listing', 'selfDeclaration'],
  ['Initial Findings', 'initialFindings'],
  ['Updated Findings', 'updatedFindings'],
  ['Compliance Level', 'complianceLevel'],
  ['Gaps Status', 'gapsStatus'],
  ['Reviewed Y/N', 'reviewed'],
];

/**
 * Canonical fields the connector cannot change on an update. Their cells are read
 * and reported, never written — see TransactionUpdateInput's Omit list. Period
 * moved out of here in Aug 2026 (owner feedback) — it is an update column now.
 */
const INSERT_ONLY_FIELDS = ['Year'] as const;

/** Does this record's workbook carry a column for `canonical`? */
function headerCarries(headers: readonly string[], canonical: string): boolean {
  const aliases = ALIASES[canonical];
  if (!aliases) return false;
  const normalised = new Set(headers.map((h) => normalizeHeader(h)));
  return aliases.some((alias) => normalised.has(normalizeHeader(alias)));
}

/** Yes/No → boolean; anything else undefined (never a guess). Twin of the import's. */
export function yesNoCell(v: string): boolean | undefined {
  const s = v.trim().toLowerCase();
  if (/^(y|yes|true|1)$/.test(s)) return true;
  if (/^(n|no|false|0)$/.test(s)) return false;
  return undefined;
}

/** Trimmed value, or undefined when blank (so the mapper omits it). */
const opt = (v: string): string | undefined => {
  const t = v.trim();
  return t === '' ? undefined : t;
};

/**
 * Map one record to its update payload, restricted to `present` — the columns the
 * workbook carries. A column not in `present` is never touched; a blank cell in a
 * present column collapses to `undefined` and is likewise omitted.
 */
export function recordToPatch(
  record: ImportRecord,
  present: ReadonlySet<BulkUpdateColumn>,
  rowNo: number,
): BulkUpdateRow {
  const warnings: string[] = [];
  const referenceNo = resolveField(record, 'Reference No');
  const patch: TransactionInsertInput = { referenceNo };
  const has = (c: BulkUpdateColumn): boolean => present.has(c);

  // Panel first: it decides the dataset (R6 — a panel release is its own bucket
  // and the flag outranks the Dataset cell).
  const panel = has('panel') ? yesNoCell(resolveField(record, 'Panel Release')) : undefined;
  if (panel !== undefined) patch.panel = panel;

  if (has('dataset')) {
    const text = resolveField(record, 'Dataset');
    // Only classify when the file actually said something, or flagged a panel —
    // otherwise inferDataset's reference-prefix fallback would silently RE-LABEL
    // a row from a blank cell, which is exactly the "blank ≠ clear" rule broken.
    if (text.trim() !== '' || panel === true) {
      patch.dataset = inferDataset(text, referenceNo, panel);
    }
  }

  if (has('value')) {
    const parsed = parseRand(resolveField(record, 'Value'));
    const raw = resolveField(record, 'Value').trim();
    if (parsed != null) {
      patch.value = parsed;
      patch.valueDisplay = parsed ? formatRand(parsed) : '';
    } else if (raw !== '') {
      warnings.push(`Value "${raw}" is not a number — left unchanged.`);
    }
  }

  const writable = patch as unknown as Record<string, unknown>;
  const text = (column: BulkUpdateColumn, canonical: string): void => {
    if (!has(column)) return;
    const v = opt(resolveField(record, canonical));
    if (v !== undefined) writable[column] = v;
  };
  // Period: fold "Quarter 1" → Q1 at the door; keep an unrecognised raw value.
  if (has('period')) {
    const v = opt(resolveField(record, 'Period'));
    if (v !== undefined) writable['period'] = normalisePeriod(v) || v;
  }
  text('division', 'Division');
  text('supplier', 'Supplier');
  text('csdNumber', 'CSD Number');
  text('mechanism', 'Mechanism');
  text('link', 'Link');
  text('ou', 'OU');
  text('contractNumber', 'Contract Number');
  text('linkIssue', 'Link Issue');
  text('bu', 'BU');
  text('briefDescription', 'Brief Description');
  text('buyer', 'Buyer');
  text('prNumber', 'PR Number');
  text('procurementManager', 'Procurement Manager');
  text('endUser', 'End-User');
  text('selfDeclaration', 'Self-declaration Listing');
  text('initialFindings', 'Initial Findings');
  text('updatedFindings', 'Updated Findings');
  text('complianceLevel', 'Compliance Level');

  if (has('linkProvided')) {
    const v = yesNoCell(resolveField(record, 'Link Provided'));
    if (v !== undefined) patch.linkProvided = v;
  }

  // Gaps status is folded onto the four-state lifecycle at the door, exactly as
  // the import does. It is normally RE-DERIVED from the findings after a merge
  // (see the page) — the parsed value only survives when the row has no findings
  // change to roll up from.
  if (has('gapsStatus')) {
    const gaps = normalizeGapStatus(resolveField(record, 'Gaps Status'));
    if (gaps) patch.gapsStatus = gaps;
  }

  // Review status and the boolean flag move TOGETHER or not at all — the
  // desynchronisation bug that still bites Manage and "Request revision".
  if (has('reviewed')) {
    const raw = opt(resolveField(record, 'Reviewed Y/N'));
    if (raw !== undefined) {
      const asFlag = yesNoCell(raw);
      const status = asFlag === undefined ? raw : asFlag ? 'Reviewed' : 'Not Reviewed';
      patch.reviewStatus = status;
      patch.reviewed = reviewedFlagForStatus(status);
    }
  }

  // Findings: the file's findings column becomes classified lines, exactly as on
  // the import path. The MERGE (analyst/CES lines preserved) happens at apply
  // time — see mergeImportFindings.
  const carriesFindings = has('updatedFindings') || has('initialFindings');
  const findingsText = has('updatedFindings')
    ? resolveField(record, 'Updated Findings') || resolveField(record, 'Initial Findings')
    : has('initialFindings')
      ? resolveField(record, 'Initial Findings')
      : '';
  const findings = splitFindings(findingsText).map((line) =>
    findingFromText(
      line,
      { mechanism: patch.mechanism, value: patch.value ?? 0 },
      String(patch.complianceLevel ?? ''),
    ),
  );

  return { rowNo, referenceNo, patch, findings, carriesFindings, warnings };
}

/** The pure matrix-level core: a workbook's cells → per-row update payloads. */
export function parseBulkUpdateMatrix(matrix: readonly (readonly string[])[]): BulkUpdateParse {
  const { headers } = detectHeaderRow(matrix.map((r) => [...r]));
  const records = matrixToRecords(matrix);

  const presentColumns = BULK_UPDATE_COLUMNS.filter((column) =>
    FIELD_TO_COLUMN.some(([canonical, c]) => c === column && headerCarries(headers, canonical)),
  );
  // valueDisplay is derived from Value, never a column of its own.
  const present = new Set<BulkUpdateColumn>(presentColumns.filter((c) => c !== 'valueDisplay'));
  if (present.has('value')) present.add('valueDisplay');

  const insertOnlyColumns = INSERT_ONLY_FIELDS.filter((f) => headerCarries(headers, f));

  const mappedHeaders = new Set<string>();
  for (const [canonical] of FIELD_TO_COLUMN) {
    for (const alias of ALIASES[canonical] ?? []) mappedHeaders.add(normalizeHeader(alias));
  }
  for (const f of INSERT_ONLY_FIELDS) {
    for (const alias of ALIASES[f] ?? []) mappedHeaders.add(normalizeHeader(alias));
  }
  for (const alias of ALIASES['Reference No']) mappedHeaders.add(normalizeHeader(alias));
  const ignoredHeaders = headers.filter(
    (h) => h.trim() !== '' && !mappedHeaders.has(normalizeHeader(h)),
  );

  const rows = records.map((record, idx) => recordToPatch(record, present, idx + 1));
  return { rows, presentColumns: [...present], insertOnlyColumns, ignoredHeaders };
}

/** SheetJS byte-level wrapper: workbook file (or CSV text) → parsed update. */
export function parseBulkUpdateWorkbook(input: ArrayBuffer | string): BulkUpdateParse {
  return parseBulkUpdateMatrix(parseWorkbook(input));
}
